import { libraryItemType } from "@corca/data/libraries/item";

import { toSuggestion } from "./toSuggestion";

import type { SingleSuggestion, SuggestionProviderType } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { LibraryItem } from "@corca/data";
import type { LibraryItemType } from "@corca/data/libraries/item";

/**
 * The provider types that library items can replace. A search that accepts any of them gets
 * library items too.
 */
export const LIBRARY_SERVES = new Set<SuggestionProviderType>([
  "library",
  "expression",
  "function",
  "serviceFunction",
  "variable",
  "constant",
  "operatorSymbol",
]);

export type Candidate = {
  suggestion: SingleSuggestion<"library">;
  type: LibraryItemType;
  service: boolean;
  single: boolean;
  multi: boolean;
};

const candidatesByItems = new WeakMap<LibraryItem[], Candidate[]>();

/**
 * The order the typed providers ran in, which decides which popup group appears first when their
 * ranks tie: operator symbols, then the symbol rows that came with the variable provider, then
 * the rest.
 */
function providerOrder(candidate: Candidate): number {
  if (candidate.type === "operatorSymbol") return 0;
  if (candidate.suggestion.item.isSymbol) return 1;
  return 2;
}

/**
 * The served items of the document as suggestions, with what the search filters on worked out
 * once. Rebuilt only when the model's `servedItems` array changes, which is on load or a key
 * edit.
 */
export function candidates(doc: DocumentModel): Candidate[] {
  const { servedItems } = doc.libraries;
  const cached = candidatesByItems.get(servedItems);
  if (cached) {
    return cached;
  }

  const built = servedItems
    .map((item) => {
      const { scope, context } = item.suggestion ?? {};

      return {
        suggestion: toSuggestion(item),
        type: libraryItemType(item),
        service: scope?.includes("service") ?? false,
        single: context?.includes("single") ?? true,
        multi: context?.includes("multi") ?? true,
      };
    })
    .sort((a, b) => providerOrder(a) - providerOrder(b));

  candidatesByItems.set(servedItems, built);

  return built;
}
